import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Inbox } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { formatCurrency, formatPercent } from '@/lib/format';

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 lg:mb-8">
    <div>
      <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight fp-page-title" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm lg:text-base fp-page-subtitle max-w-2xl">{subtitle}</p>}
    </div>
    {actions}
  </div>
);

export const Delta = ({ value, up, negativeDelta, className = '' }) => {
  const { lang } = useApp();
  const good = negativeDelta ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${good ? 'text-emerald-600' : 'text-rose-500'} ${className}`}>
      <Icon className="w-3.5 h-3.5" /> {formatPercent(value, lang)}
    </span>
  );
};

export const Card = ({ children, className = '', hover = false, ...props }) => (
  <div
    className={`bg-white/[0.96] backdrop-blur-xl rounded-xl border border-white/80 shadow-sm ${hover ? 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:bg-white' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

export const KpiCard = ({ item, index = 0 }) => {
  const { t, lang } = useApp();
  const Icon = item.icon;
  const display = item.unit === 'pct' ? `${item.value}%` : formatCurrency(item.value, lang);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
    >
      <Card hover className="p-5" data-testid={`kpi-${item.key}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] tracking-[0.15em] uppercase font-semibold text-zinc-400">{t(`finance.${item.key}`)}</span>
          <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center">
            <Icon className="w-4 h-4 text-cyan-600" />
          </div>
        </div>
        <p className="font-heading text-2xl lg:text-[28px] font-bold text-zinc-900 font-mono-num" data-testid={`kpi-${item.key}-value`}>{display}</p>
        <div className="mt-2 flex items-center gap-1.5">
          <Delta value={item.delta} up={item.up} negativeDelta={item.negativeDelta} />
          <span className="text-[11px] text-zinc-400">{t('common.vsPrev')}</span>
        </div>
      </Card>
    </motion.div>
  );
};

export const NoData = ({ className = '' }) => {
  const { t } = useApp();
  return (
    <div className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}>
      <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
        <Inbox className="w-6 h-6 text-zinc-400" />
      </div>
      <p className="font-heading font-semibold text-zinc-500 tracking-widest text-sm">{t('common.noData')}</p>
      <p className="text-xs text-zinc-400 mt-1 max-w-xs">{t('common.noDataDesc')}</p>
    </div>
  );
};

export const severityStyles = {
  critical: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', dot: 'bg-rose-500', chip: 'bg-rose-100 text-rose-700' },
  important: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', chip: 'bg-amber-100 text-amber-700' },
  attention: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-700' },
  information: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500', chip: 'bg-blue-100 text-blue-700' },
  opportunity: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', dot: 'bg-violet-500', chip: 'bg-violet-100 text-violet-700' },
};

export const Stagger = ({ children, className = '' }) => (
  <motion.div
    className={className}
    initial="hidden"
    animate="show"
    variants={{ show: { transition: { staggerChildren: 0.05 } } }}
  >
    {children}
  </motion.div>
);
